import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ɵInternalFormsSharedModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { Donation, Survey } from '@domains';
import { AppService } from '@donor/app.service';
import {
  Designable,
  DesignService,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  ResponsiveService,
} from '@rspl-ui';
import { take } from 'rxjs';

@Component({
  selector: 'app-survey-dialog',
  templateUrl: './survey-dialog.component.html',
  styleUrls: ['./survey-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    FxFlexDirective,
    MatInput,
    ɵInternalFormsSharedModule,
    ReactiveFormsModule,
    FxFlexDirective,
    FxLayoutGapDirective,
    MatError,
    MatSelect,
    MatOption,
  ],
})
export class SurveyDialogComponent extends Designable implements OnInit {
  text = new FormControl<string>('', Validators.required);
  select = new FormControl<string>('', Validators.required);
  answers: string[] = [];

  public constructor(
    public reference: MatDialogRef<SurveyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { survey: Survey; donation: Donation },
    private appService: AppService,
    protected override designService: DesignService,
    public override responsiveService: ResponsiveService,
  ) {
    super(designService, responsiveService);
    if (data.survey.randomize) {
      this.randomizeAnswers();
    } else {
      this.answers = data.survey.answers;
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.appService
      .createDonationSurveyActivity(this.data.donation.id, this.data.survey.trigger, 'displayed', this.data.survey.question)
      .pipe(take(1))
      .subscribe();
  }

  skip() {
    this.appService
      .createDonationSurveyActivity(this.data.donation.id, this.data.survey.trigger, 'closed', this.data.survey.question)
      .pipe(take(1))
      .subscribe();
    this.reference.close(this.data.donation);
  }

  public submit(): void {
    let response: string;
    if (this.data.survey.isSelect) {
      this.select.markAllAsTouched();
      this.select.updateValueAndValidity();
      if (this.select.invalid) {
        return;
      }
      response = this.select.value;
    }
    if (!this.data.survey.isSelect || (this.data.survey.isSelect && this.select.value === 'OTHER')) {
      this.text.markAllAsTouched();
      this.text.updateValueAndValidity();
      if (this.text.invalid) {
        return;
      }
      response = this.text.value;
    }

    this.appService
      .createDonationSurveyActivity(this.data.donation.id, this.data.survey.trigger, 'answered', response)
      .pipe(take(1))
      .subscribe(() => {
        const donation = new Donation(this.data.donation);
        donation.meta.completedSurveys = [...new Set([...donation.meta.completedSurveys, this.data.survey.trigger])];
        this.appService
          .updateDonationByCode(donation)
          .pipe(take(1))
          .subscribe((d) => {
            this.reference.close(d);
          });
      });
  }

  randomizeAnswers() {
    const shuffled = [...this.data.survey.answers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    this.answers = shuffled;
  }
}
